import type { FundColumnEdit, FundSummary } from '@nemo/api';
import type { FundStageView, StageChoice } from '@nemo/core/model';
import type { PicklistOption } from './cells/index.js';

/**
 * What a committed cell asks the api to do. Sectors and owners are join tables,
 * and stages are rows per source, so each is its own procedure rather than a
 * column update.
 */
export type FundWrite =
  | { kind: 'edits'; edits: readonly FundColumnEdit[] }
  | { kind: 'sectors'; sectorIds: readonly string[] }
  | { kind: 'owners'; memberIds: readonly string[] }
  | { kind: 'stages'; stages: readonly StageChoice[] };

export interface FundLists {
  sectors: readonly PicklistOption[];
  members: readonly PicklistOption[];
}

/** The row as it will be once the write lands, so the grid can show it now. */
export function applyWriteLocally(
  fund: FundSummary,
  write: FundWrite,
  lists: FundLists,
): FundSummary {
  switch (write.kind) {
    case 'sectors':
      return { ...fund, sectors: labelsForIds(write.sectorIds, lists.sectors) };
    case 'owners':
      return { ...fund, owners: labelsForIds(write.memberIds, lists.members) };
    case 'stages':
      return { ...fund, stages: stagesAfter(fund.stages, write.stages) };
    case 'edits':
      return write.edits.reduce(applyEdit, fund);
  }
}

/**
 * The stages as the server will answer once a person's choices land: each
 * changed stage decided by a person's row, the rest as they were.
 */
function stagesAfter(
  current: readonly FundStageView[],
  choices: readonly StageChoice[],
): FundStageView[] {
  const byStage = new Map(current.map((entry) => [entry.stage, entry]));
  for (const choice of choices) {
    const entry = byStage.get(choice.stage);
    if (entry?.participates === choice.participates && entry.leads === choice.leads) continue;
    if (entry === undefined && !choice.participates && !choice.leads) continue;
    const person = {
      source: 'person' as const,
      invests: choice.participates ? ('asserts' as const) : ('denies' as const),
      leads: choice.leads ? ('asserts' as const) : ('denies' as const),
    };
    byStage.set(choice.stage, {
      stage: choice.stage,
      participates: choice.participates,
      leads: choice.leads,
      sources: [person, ...(entry?.sources ?? []).filter((fact) => fact.source !== 'person')],
      observed: entry?.observed ?? null,
      // The answer is newer than any evidence now, which is what settles a conflict.
      conflicts: [],
    });
  }
  return [...byStage.values()];
}

function applyEdit(fund: FundSummary, edit: FundColumnEdit): FundSummary {
  switch (edit.column) {
    case 'name':
      return { ...fund, name: edit.value };
    case 'tier':
      return { ...fund, tier: edit.value };
    case 'listStatus':
      return { ...fund, listStatus: edit.value };
    case 'stagesLed':
      return { ...fund, stagesLed: [...edit.value] };
    case 'leadStatus':
      return { ...fund, leadStatus: edit.value };
    case 'hqCountries':
      return { ...fund, hqCountries: [...edit.value] };
    case 'notableInvestments':
      return { ...fund, notableInvestments: [...edit.value] };
    case 'chequeMin':
      return { ...fund, chequeMin: edit.value };
    case 'chequeMax':
      return { ...fund, chequeMax: edit.value };
    case 'chequeCurrency':
      return { ...fund, chequeCurrency: edit.value };
    case 'website':
      return { ...fund, website: edit.value };
    case 'notes':
      return { ...fund, notes: edit.value };
    case 'investorType':
      return { ...fund, investorType: edit.value };
    // Which firm Dealroom thinks this row is. The grid has no cell for it, so
    // there is no local echo to show; it is written through MCP and read back
    // on the next load.
    case 'dealroomId':
      return fund;
  }
}

export function labelsForIds(ids: readonly string[], options: readonly PicklistOption[]): string[] {
  const byId = new Map(options.map((option) => [option.id, option.label]));
  return ids
    .flatMap((id) => {
      const label = byId.get(id);
      return label === undefined ? [] : [label];
    })
    .sort((left, right) => left.localeCompare(right));
}

export function idsForLabels(
  labels: readonly string[],
  options: readonly PicklistOption[],
): string[] {
  const byLabel = new Map(options.map((option) => [option.label, option.id]));
  return labels.flatMap((label) => {
    const id = byLabel.get(label);
    return id === undefined ? [] : [id];
  });
}
